/**
 * ״קצה גלילה״ לפסים דביקים (6ו, A-154; סדר העבודה §12): במקום קו קבוע מתחת לפס, צל רך
 * שמופיע רק כשיש תוכן מתחתיו (או מעליו — לשורה התחתונה).
 *
 * האלמנט הנצפה הוא sentinel קטן בזרימת הדף, ליד הפס: כשהוא יוצא מהחלון **למעלה או למטה** —
 * הפס ״דבוק״ ומקבל `is-stuck`. IntersectionObserver — בלי מאזין גלילה, בלי עבודה בכל מסגרת.
 * דפדפן בלי IntersectionObserver — הפס פשוט בלי צל.
 *
 * 6ו-ב חלק 5 (A-189): במעבר רשימה ↔ ביקור המסך הנכנס נכנס מהצד (translateX), וה־sentinel שבתוכו
 * יוצא מהחלון **לרוחב** לרגע — זו אינה גלילה, והפס לא היה אמור להבהב למצב ״דבוק״ בזמן הכניסה.
 * ולכן ״דבוק״ נקבע רק לפי הציר האנכי: ה־sentinel מעל קצה השורש או מתחתיו.
 */

import { useEffect, useState, type RefObject } from 'react';

/** מחוץ לשורש בציר האנכי (מעל או מתחת, אחרי ה־rootMargin) — ולא רק לרוחב. */
function outsideVertically(entry: IntersectionObserverEntry): boolean {
  const root = entry.rootBounds;
  const r = entry.boundingClientRect;
  return root === null || r.bottom <= root.top || r.top >= root.bottom;
}

export function useStuck(
  sentinel: RefObject<HTMLElement | null>,
  /** שולי החלון להתחשב בהם (למשל גובה השורה התחתונה) — תחביר `rootMargin`. */
  rootMargin = '0px',
): boolean {
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    const el = sentinel.current;
    if (el === null || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry !== undefined) setStuck(!entry.isIntersecting && outsideVertically(entry));
      },
      { rootMargin, threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [sentinel, rootMargin]);
  return stuck;
}
